/**
 * Frontmatter parse / serialize for the documents header "properties" panel.
 *
 * A markdown file may carry an optional leading YAML-ish frontmatter block — a
 * `---` line, flat `key: value` lines, a closing `---` line — exactly the shape
 * the core `parseMarkdownDocument` recognizes. The properties panel in the
 * document header reads + writes that block; this module is the round-trippable
 * bridge between the raw file source and an ordered `key: value` list.
 *
 * Only flat `key: value` is supported (matching how the rest of the codebase
 * treats frontmatter). Values are kept verbatim except for surrounding quotes,
 * which are stripped on parse and re-added on serialize only when the value
 * needs them (leading/trailing space, a `:` or `#`, or it would otherwise be
 * ambiguous).
 */

export interface FrontmatterField {
  readonly key: string;
  readonly value: string;
}

export interface ParsedFrontmatter {
  /** Ordered key/value fields from the leading block (empty if none). */
  readonly fields: readonly FrontmatterField[];
  /** Whether the source actually had a `---`…`---` block. */
  readonly hasBlock: boolean;
  /** The raw body (everything after the closing fence), verbatim. */
  readonly body: string;
}

const OPEN = /^---\r?\n/;

/** Find the closing-fence geometry of a leading frontmatter block. Returns the
 *  offset where the inner block starts, the offset of the closing `---` line,
 *  and where the body begins — or null when there is no well-formed block. */
function locateBlock(
  source: string,
): { innerStart: number; closeStart: number; bodyStart: number } | null {
  if (!OPEN.test(source)) return null;
  const openEnd = source.indexOf("\n"); // end of the opening `---` line
  let scan = openEnd + 1;
  while (scan <= source.length) {
    const nl = source.indexOf("\n", scan);
    const lineEnd = nl === -1 ? source.length : nl;
    if (source.slice(scan, lineEnd).trim() === "---") {
      return {
        innerStart: openEnd + 1,
        closeStart: scan,
        bodyStart: nl === -1 ? source.length : nl + 1,
      };
    }
    if (nl === -1) break;
    scan = nl + 1;
  }
  return null;
}

/** Parse the leading frontmatter block of a markdown source into ordered
 *  fields + the body. When there is no block, `fields` is empty and `body` is
 *  the whole source. */
export function parseFrontmatter(source: string): ParsedFrontmatter {
  const loc = locateBlock(source);
  if (!loc) return { fields: [], hasBlock: false, body: source };
  const block = source.slice(loc.innerStart, loc.closeStart);
  const fields: FrontmatterField[] = [];
  for (const line of block.split("\n")) {
    if (line.trim() === "") continue;
    const colon = line.indexOf(":");
    if (colon === -1) continue;
    const key = line.slice(0, colon).trim();
    if (!key || /\s/.test(key)) continue;
    let value = line.slice(colon + 1).trim();
    if (
      (value.startsWith('"') && value.endsWith('"') && value.length >= 2) ||
      (value.startsWith("'") && value.endsWith("'") && value.length >= 2)
    ) {
      value = value.slice(1, -1);
    }
    fields.push({ key, value });
  }
  return { fields, hasBlock: true, body: source.slice(loc.bodyStart) };
}

/** Does a frontmatter value need to be quoted to survive a round-trip? */
function needsQuotes(value: string): boolean {
  if (value === "") return false;
  if (value !== value.trim()) return true; // leading/trailing space
  return /[:#]/.test(value);
}

function serializeValue(value: string): string {
  if (!needsQuotes(value)) return value;
  // Prefer double quotes; escape any embedded double quotes.
  return `"${value.replace(/"/g, '\\"')}"`;
}

/**
 * Rebuild a markdown source from an ordered set of frontmatter fields + the
 * existing source (whose body is preserved).
 *
 * - With fields and no existing block → a `---`…`---` block is prepended.
 * - With fields and an existing block → the block is rewritten in place.
 * - With NO fields → any existing block is removed (body only).
 *
 * Empty-key fields are dropped (an in-progress new row the user never named).
 */
export function serializeFrontmatter(
  source: string,
  fields: readonly FrontmatterField[],
): string {
  const { body } = parseFrontmatter(source);
  const clean = fields.filter((f) => f.key.trim() !== "");

  if (clean.length === 0) return body;

  const lines = clean.map((f) => `${f.key.trim()}: ${serializeValue(f.value)}`);
  return `---\n${lines.join("\n")}\n---\n${body}`;
}

/** Set / add / remove a single key, returning the rewritten source. A
 *  `value === null` removes the key. Preserves field order; a new key is
 *  appended. */
export function setFrontmatterField(
  source: string,
  key: string,
  value: string | null,
): string {
  const { fields } = parseFrontmatter(source);
  const next: FrontmatterField[] = [];
  let found = false;
  for (const f of fields) {
    if (f.key === key) {
      found = true;
      if (value !== null) next.push({ key, value });
    } else {
      next.push(f);
    }
  }
  if (!found && value !== null) next.push({ key, value });
  return serializeFrontmatter(source, next);
}
